import React from 'react';

const SelectField = ({
  label,
  name,
  value,
  onChange,
  options = [],
  optionValue = 'value',
  optionLabel = 'label',
  placeholder = 'Select an option...',
  required = false,
  disabled = false,
  error,
  helpText,
  className = '',
  allowEmpty = true,
  loading = false,
  ...props
}) => {
  // Debug: Log the options being passed to this component
  if (name === 'reporter_department_id' || name === 'location_id' || name === 'event_category_id') {
    console.log(`DEBUG SelectField [${name}]:`, {
      optionsLength: options?.length || 0,
      options: options,
      optionValue,
      optionLabel,
      loading,
      firstOption: options?.[0]
    });
  }

  // Test: Add hardcoded options for debugging if no options are provided
  let testOptions = options;
  if (name === 'reporter_department_id' && (!options || options.length === 0)) {
    testOptions = [
      { id: 'test-1', dept_name: 'TEST: Line Maintenance' },
      { id: 'test-2', dept_name: 'TEST: Heavy Maintenance' },
      { id: 'test-3', dept_name: 'TEST: Quality Assurance' }
    ];
    console.log('Using test options for department dropdown');
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    onChange({ [name]: value === '' ? null : value });
  };

  const fieldId = `select-${name}`;
  const hasError = Boolean(error);
  
  const baseSelectClasses = `
    w-full px-3 py-2 border rounded-lg shadow-sm transition-colors duration-200
    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
    disabled:bg-gray-100 disabled:cursor-not-allowed
    appearance-none bg-white
    ${hasError 
      ? 'border-red-300 bg-red-50 focus:ring-red-500 focus:border-red-500' 
      : 'border-gray-300 bg-white hover:border-gray-400'
    }
  `;

  return (
    <div className={`space-y-1 ${className}`}>
      {/* Label */}
      {label && (
        <label 
          htmlFor={fieldId}
          className={`block text-sm font-medium ${hasError ? 'text-red-700' : 'text-gray-700'}`}
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Select Field */}
      <div className="relative">
        <select
          id={fieldId}
          name={name}
          value={value || ''}
          onChange={handleChange}
          required={required}
          disabled={disabled || loading}
          className={baseSelectClasses.trim()}
          {...props}
        >
          {/* Empty option */}
          {allowEmpty && (
            <option value="">
              {loading ? 'Loading...' : placeholder}
            </option>
          )}
          
          {/* Options */}
          {testOptions.map((option, index) => {
            const optVal = typeof option === 'object' ? option[optionValue] : option;
            const optLabel = typeof option === 'object' ? option[optionLabel] : option;
            
            return (
              <option key={optVal || index} value={optVal}>
                {optLabel}
              </option>
            );
          })}
        </select>

        {/* Dropdown Arrow */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          {loading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          ) : (
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </div>
      </div>

      {/* Help Text */}
      {helpText && !hasError && (
        <p className="text-sm text-gray-500">{helpText}</p>
      )}

      {/* Error Message */}
      {hasError && (
        <p className="text-sm text-red-600 flex items-center">
          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {error}
        </p>
      )}

      {/* Loading State */}
      {loading && options.length === 0 && (
        <p className="text-sm text-gray-500 animate-pulse">Loading options...</p>
      )}
    </div>
  );
};

export default SelectField;
